import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  query,
  setDoc,
  where,
} from 'firebase/firestore';
import { db } from '@/services/firebase';
import { importEcdhPairFromBackup } from '@/services/chatCryptoKeys';
import { fetchP2PContactsOnce } from '@/services/chatContactsFirestore';
import { parseSortedThreadInviteTokens, p2pThreadId } from '@/services/p2pThreadInvitePath';

export type ChatEcdhKeyPairBackup = { privateJwk: JsonWebKey; publicJwk: JsonWebKey };

const FIELD_INVITE = 'chatInviteTokenBackup';
const FIELD_KEYPAIR = 'chatEcdhKeyPairBackup';

const INVITE_LOOKUP = 'inviteLookup';
const THREADS = 'p2pChatThreads';
const MESSAGES = 'messages';

function newInviteToken(): string {
  return typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `inv_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

async function listInviteLookupDocIdsForUid(uid: string): Promise<string[]> {
  const q = query(collection(db, INVITE_LOOKUP), where('uid', '==', uid), limit(100));
  const snap = await getDocs(q);
  const ids: string[] = [];
  snap.forEach((d) => ids.push(d.id));
  return ids;
}

async function threadHasAnyMessage(threadId: string): Promise<boolean> {
  const mq = query(collection(db, THREADS, threadId, MESSAGES), limit(1));
  const snap = await getDocs(mq);
  return !snap.empty;
}

/**
 * Если в users нет бэкапа токена, а в inviteLookup пусто — восстанавливаем СВОЙ токен по id треда:
 * ищем треды, где в **`participantInviteTokens`** есть токен контакта; читать их может только участник (правила Firestore).
 */
async function recoverMyInviteTokenFromThreadIds(uid: string): Promise<string | null> {
  const contacts = await fetchP2PContactsOnce(uid);
  if (contacts.length === 0) return null;
  const peerSet = new Set(contacts.map((c) => c.id).filter(Boolean));

  for (const peer of Array.from(peerSet)) {
    try {
      const snap = await getDocs(
        query(
          collection(db, THREADS),
          where('participantInviteTokens', 'array-contains', peer),
          limit(50)
        )
      );
      for (const d of snap.docs) {
        const parsed = parseSortedThreadInviteTokens(d.id);
        if (!parsed) continue;
        const [ta, tb] = parsed;
        const aIsPeer = peerSet.has(ta);
        const bIsPeer = peerSet.has(tb);
        if (aIsPeer && !bIsPeer) return tb;
        if (bIsPeer && !aIsPeer) return ta;
      }
    } catch {
      /* индекс / сеть / правила */
    }
  }
  return null;
}

async function pickInviteTokenWithHistory(uid: string, candidates: string[]): Promise<string | null> {
  if (candidates.length === 0) return null;
  if (candidates.length === 1) return candidates[0];
  const contacts = await fetchP2PContactsOnce(uid);
  const peerTokens = contacts.map((c) => c.id).filter(Boolean);
  if (peerTokens.length === 0) return candidates[0];
  for (const t of candidates) {
    for (const peer of peerTokens) {
      if (peer === t) continue;
      const tid = p2pThreadId(t, peer);
      if (await threadHasAnyMessage(tid)) return t;
    }
  }
  return candidates[0];
}

function isJwkShape(x: unknown): x is JsonWebKey {
  return typeof x === 'object' && x !== null && typeof (x as JsonWebKey).kty === 'string';
}

function isValidKeyPairBackup(x: unknown): x is ChatEcdhKeyPairBackup {
  if (typeof x !== 'object' || x === null) return false;
  const o = x as Record<string, unknown>;
  return isJwkShape(o.privateJwk) && isJwkShape(o.publicJwk);
}

/**
 * Загружает invite-токен и ECDH-пару: только Firestore + генерация при первом входе.
 * Без localStorage — вызывать до рендера чата; затем `setSessionChatKeyPair` / `setSessionInviteToken`.
 */
export async function loadChatIdentityFromFirestore(uid: string): Promise<{
  inviteToken: string;
  keyPair: CryptoKeyPair;
}> {
  if (!uid) {
    throw new Error('loadChatIdentityFromFirestore: empty uid');
  }
  if (typeof crypto === 'undefined' || !crypto.subtle) {
    throw new Error('Web Crypto unavailable');
  }

  const snap = await getDoc(doc(db, 'users', uid));
  const d = snap.exists() ? (snap.data() as Record<string, unknown>) : {};

  let inviteToken = typeof d[FIELD_INVITE] === 'string' ? d[FIELD_INVITE].trim() : '';
  if (!inviteToken) {
    const candidates = await listInviteLookupDocIdsForUid(uid);
    const chosen = await pickInviteTokenWithHistory(uid, candidates);
    inviteToken = chosen ?? '';
  }
  if (!inviteToken) {
    inviteToken = (await recoverMyInviteTokenFromThreadIds(uid)) ?? '';
  }
  if (!inviteToken) {
    inviteToken = newInviteToken();
  }

  const kp = d[FIELD_KEYPAIR];
  let keyPair: CryptoKeyPair;
  if (isValidKeyPairBackup(kp)) {
    try {
      keyPair = await importEcdhPairFromBackup(kp);
    } catch {
      keyPair = await crypto.subtle.generateKey(
        { name: 'ECDH', namedCurve: 'P-256' },
        true,
        ['deriveBits']
      );
    }
  } else {
    keyPair = await crypto.subtle.generateKey(
      { name: 'ECDH', namedCurve: 'P-256' },
      true,
      ['deriveBits']
    );
  }

  return { inviteToken, keyPair };
}

export async function persistChatIdentityBackup(
  uid: string,
  inviteToken: string,
  keyPair: ChatEcdhKeyPairBackup
): Promise<void> {
  if (!uid || !inviteToken?.trim() || !isValidKeyPairBackup(keyPair)) return;
  const trimmed = inviteToken.trim();
  const ref = doc(db, 'users', uid);
  const existingSnap = await getDoc(ref);
  const prevInv =
    existingSnap.exists() &&
    typeof (existingSnap.data() as Record<string, unknown>)[FIELD_INVITE] === 'string'
      ? String((existingSnap.data() as Record<string, unknown>)[FIELD_INVITE]).trim()
      : '';
  if (prevInv && prevInv !== trimmed) {
    return;
  }
  await setDoc(
    ref,
    {
      [FIELD_INVITE]: trimmed,
      [FIELD_KEYPAIR]: keyPair,
    },
    { merge: true }
  );
}
